/**
 * kb-materials-tab.tsx — Tab "Ảnh và video" của một kho: ảnh / video / âm thanh / tệp đính kèm làm ảnh và video hỗ trợ.
 * Mỗi ảnh và video có tên, MÔ TẢ (để AI biết khi nào gửi cho khách), nhóm = cây NHÓM riêng của Ảnh và video (không dùng chung với Tài liệu / Hỏi đáp). Tải lên nhiều tệp, sửa, xoá.
 * Lưu bằng hạ tầng tải tệp của thư viện tài liệu (/uploads/doc-assets). AI CHƯA tự gửi ảnh và video (việc dở: công cụ send_material).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CheckCircle2, ExternalLink, FileText, FileUp, Loader2, Music, Pencil, Search, Trash2, Upload, Video, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch, Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { formatBytes } from '@/lib/kb-status'
import { cn } from '@/lib/utils'
import { useKnowledgeCategories } from '@/hooks/use-knowledge'
import {
  useKnowledgeMaterials, uploadKnowledgeMaterial, useUpdateKnowledgeMaterial, useDeleteKnowledgeMaterial, type KnowledgeMaterial,
} from '@/hooks/use-knowledge-bases'
import { assetUrl } from '@/lib/asset-url'
import { KbCategoryTree } from './kb-category-tree'
import { KbCategorySelect } from './kb-category-select'
import { descendantIds, MATERIAL_ACCEPT, MATERIAL_KIND_LABELS, checkMaterialFile, type KnowledgePerms } from './kb-lib'

export function KbMaterialsTab({ baseId, perms, categoryId, onCategory, openUpload, onOpenUploadHandled }: {
  baseId: string
  perms: KnowledgePerms
  categoryId: string
  onCategory: (id: string) => void
  /** true → mở hộp tải ảnh và video một lần (từ màn chào kho trống), rồi gọi onOpenUploadHandled. */
  openUpload?: boolean
  onOpenUploadHandled?: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const { data: cats } = useKnowledgeCategories(baseId, { kind: 'material' })
  const { data: items, isLoading, isError } = useKnowledgeMaterials(baseId)
  const del = useDeleteKnowledgeMaterial()
  const { canCreate, canUpdate, canDelete } = perms
  const [search, setSearch] = useState('')
  const [uploading, setUploading] = useState(false)
  const [editing, setEditing] = useState<KnowledgeMaterial | null>(null)
  useEffect(() => {
    if (!openUpload) return
    setUploading(true)
    onOpenUploadHandled?.()
  }, [openUpload, onOpenUploadHandled])

  const scope = useMemo(() => (categoryId ? new Set(descendantIds(cats ?? [], categoryId)) : null), [cats, categoryId])
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (items ?? []).filter((m) => (!scope || (m.categoryId && scope.has(m.categoryId)))
      && (!q || m.name.toLowerCase().includes(q) || (m.description ?? '').toLowerCase().includes(q)))
  }, [items, scope, search])
  const catName = (id: string | null) => (cats ?? []).find((c) => c.id === id)?.name

  async function remove(m: KnowledgeMaterial) {
    if (!window.confirm(t('materials.confirmDelete', { name: m.name }))) return
    try { await del.mutateAsync(m.id); toast.success(t('materials.deleted')) } catch (err) { toast.error(apiError(err)) }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
      <KbCategoryTree baseId={baseId} kind="material" selected={categoryId} onSelect={onCategory} perms={perms} totalCount={(items ?? []).length} />
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8" placeholder={t('materials.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {canCreate && <Button onClick={() => setUploading(true)}><Upload className="h-4 w-4" /> {t('materials.upload')}</Button>}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {t('materials.intro')}
        </p>

        {isLoading && !items ? (
          <Loading label={t('materials.loading')} />
        ) : isError ? (
          <ErrorState message={t('materials.loadError')} />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
            {search ? t('materials.emptySearch') : categoryId ? t('materials.emptyGroup') : canCreate ? t('materials.emptyCanCreate') : t('materials.empty')}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((m) => (
              <div key={m.id} className={cn('group flex flex-col overflow-hidden rounded-xl border bg-card', !m.enabled && 'opacity-60')}>
                <MaterialPreview m={m} />
                <div className="flex flex-1 flex-col gap-1 p-3">
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px]">{MATERIAL_KIND_LABELS[m.kind] ?? m.kind}</Badge>
                    {catName(m.categoryId) && <Badge variant="secondary" className="max-w-[140px] truncate text-[10px]">{catName(m.categoryId)}</Badge>}
                    {!m.enabled && <Badge variant="secondary" className="text-[10px]">{t('materials.off')}</Badge>}
                  </div>
                  <p className="truncate text-sm font-medium" title={m.name}>{m.name}</p>
                  {m.description
                    ? <p className="line-clamp-2 text-xs text-muted-foreground">{m.description}</p>
                    : <p className="text-xs text-warning">{t('materials.noDescription')}</p>}
                  <div className="mt-auto flex items-center gap-1 pt-1 text-[11px] text-muted-foreground">
                    <span className="flex-1">{formatBytes(m.fileSize)}</span>
                    <a href={assetUrl(m.fileUrl)} target="_blank" rel="noopener" className="rounded p-1 hover:bg-muted" title={t('materials.openFile')}><ExternalLink className="h-3.5 w-3.5" /></a>
                    {canUpdate && <button type="button" className="rounded p-1 hover:bg-muted" title={t('common:actions.edit')} onClick={() => setEditing(m)}><Pencil className="h-3.5 w-3.5" /></button>}
                    {canDelete && <button type="button" className="rounded p-1 text-destructive hover:bg-muted" title={t('common:actions.delete')} onClick={() => void remove(m)}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {uploading && <MaterialUploadDialog baseId={baseId} categoryId={categoryId} onClose={() => setUploading(false)} />}
      {editing && <MaterialEditDialog m={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

function MaterialPreview({ m }: { m: KnowledgeMaterial }) {
  if (m.kind === 'image') return <img src={assetUrl(m.fileUrl)} alt={m.name} className="h-36 w-full bg-muted object-cover" loading="lazy" />
  const Icon = m.kind === 'video' ? Video : m.kind === 'audio' ? Music : FileText
  return (
    <div className="flex h-36 w-full items-center justify-center bg-muted/60 text-muted-foreground">
      <Icon className="h-10 w-10" />
    </div>
  )
}

type UploadItem = { key: string; file: File; state: 'waiting' | 'uploading' | 'done' | 'error'; progress: number; message?: string }

function MaterialUploadDialog({ baseId, categoryId, onClose }: { baseId: string; categoryId: string; onClose: () => void }) {
  const { t } = useTranslation('aiKnow')
  const qc = useQueryClient()
  const ref = useRef<HTMLInputElement>(null)
  const [items, setItems] = useState<UploadItem[]>([])
  const [cat, setCat] = useState(categoryId)
  const [description, setDescription] = useState('')
  const [running, setRunning] = useState(false)
  const [drag, setDrag] = useState(false)

  function add(list: FileList | File[]) {
    const next: UploadItem[] = []
    for (const f of Array.from(list)) {
      const err = checkMaterialFile(f)
      next.push({ key: `${f.name}-${f.size}-${Math.random()}`, file: f, state: err ? 'error' : 'waiting', progress: 0, message: err ?? undefined })
    }
    setItems((p) => [...p, ...next])
  }
  const patch = (key: string, p: Partial<UploadItem>) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...p } : i)))

  async function run() {
    const queue = items.filter((i) => i.state === 'waiting')
    if (!queue.length) { toast.error(t('materials.noFiles')); return }
    setRunning(true)
    for (const it of queue) {
      patch(it.key, { state: 'uploading' })
      try {
        await uploadKnowledgeMaterial(baseId, it.file, {
          categoryId: cat || null, description: description.trim() || undefined,
          onProgress: (pct) => patch(it.key, { progress: pct }),
        })
        patch(it.key, { state: 'done', progress: 100 })
      } catch (err) {
        patch(it.key, { state: 'error', message: apiError(err) })
      }
    }
    setRunning(false)
    await qc.invalidateQueries({ queryKey: ['knowledge'] })
  }

  const waiting = items.filter((i) => i.state === 'waiting').length
  const done = items.filter((i) => i.state === 'done').length

  return (
    <Dialog open onOpenChange={(o) => !o && !running && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('materials.upload')}</DialogTitle>
          <DialogDescription>{t('materials.uploadDescription')}</DialogDescription>
        </DialogHeader>
        <div
          role="button" tabIndex={0}
          onClick={() => ref.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') ref.current?.click() }}
          onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) add(e.dataTransfer.files) }}
          className={cn('flex cursor-pointer flex-col items-center gap-1 rounded-lg border-2 border-dashed px-4 py-7 text-center', drag ? 'border-primary bg-primary/5' : 'hover:border-primary/60 hover:bg-muted/40')}
        >
          <FileUp className="h-7 w-7 text-primary" />
          <p className="text-sm font-medium">{t('materials.dropHere')}</p>
          <p className="text-xs text-muted-foreground">{t('materials.dropFormats')}</p>
        </div>
        <input ref={ref} type="file" multiple accept={MATERIAL_ACCEPT} hidden onChange={(e) => { if (e.target.files?.length) add(e.target.files); e.target.value = '' }} />
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <Label>{t('materials.group')}</Label>
            <KbCategorySelect knowledgeBaseId={baseId} kind="material" value={cat} onChange={setCat} noneLabel={t('materials.noGroup')} />
          </div>
          <div className="sm:col-span-2">
            <Label>{t('materials.commonDescription')}</Label>
            <Textarea className="min-h-16 text-sm" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('materials.commonDescriptionPlaceholder')} />
          </div>
        </div>
        {items.length > 0 && (
          <ul className="space-y-1.5">
            {items.map((it) => (
              <li key={it.key} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm">
                {it.state === 'done' ? <CheckCircle2 className="h-4 w-4 text-success" /> : it.state === 'error' ? <XCircle className="h-4 w-4 text-destructive" /> : it.state === 'uploading' ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <FileUp className="h-4 w-4 text-muted-foreground" />}
                <span className="min-w-0 flex-1 truncate">{it.file.name}</span>
                <span className={cn('shrink-0 text-[11px]', it.state === 'error' ? 'text-destructive' : 'text-muted-foreground')}>
                  {it.state === 'uploading' ? `${it.progress}%` : it.state === 'error' ? it.message : it.state === 'done' ? t('materials.uploaded') : formatBytes(it.file.size)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">{done ? t('materials.uploadedCount', { n: done }) : ''}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={running}>{t('common:actions.close')}</Button>
            <Button onClick={() => void run()} disabled={running || waiting === 0}>
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {waiting ? t('materials.uploadN', { n: waiting }) : t('common:actions.upload')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function MaterialEditDialog({ m, onClose }: { m: KnowledgeMaterial; onClose: () => void }) {
  const { t } = useTranslation('aiKnow')
  const update = useUpdateKnowledgeMaterial()
  const [name, setName] = useState(m.name)
  const [description, setDescription] = useState(m.description ?? '')
  const [cat, setCat] = useState(m.categoryId ?? '')
  const [enabled, setEnabled] = useState(m.enabled)

  async function save() {
    if (!name.trim()) { toast.error(t('materials.enterName')); return }
    try {
      await update.mutateAsync({ id: m.id, name: name.trim(), description: description.trim() || null, categoryId: cat || null, enabled })
      toast.success(t('materials.saved'))
      onClose()
    } catch (err) { toast.error(apiError(err)) }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('materials.editTitle')}</DialogTitle>
          <DialogDescription>{MATERIAL_KIND_LABELS[m.kind] ?? m.kind} · {formatBytes(m.fileSize)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('materials.nameLabel')}</Label><Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} /></div>
          <div>
            <Label>{t('materials.descriptionLabel')}</Label>
            <Textarea className="min-h-24 text-sm" maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('materials.descriptionPlaceholder')} />
          </div>
          <div><Label>{t('materials.group')}</Label><KbCategorySelect knowledgeBaseId={m.knowledgeBaseId} kind="material" value={cat} onChange={setCat} noneLabel={t('materials.noGroup')} /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={enabled} onCheckedChange={setEnabled} /> {t('materials.aiUse')}</label>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button onClick={() => void save()} disabled={update.isPending}>{update.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('common:actions.save')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
